import { isBelowBreakpoint } from '@umbrella/utils';
/**
 * Filters Drawer Custom Element
 *
 * - Handles opening/closing the filters drawer (via [data-filters-toggle] buttons)
 * - Intercepts filter form submission
 * - Uses the Shopify section rendering API to load updated collection results
 * - Updates the current URL to reflect active filters
 */

class FiltersDrawer extends HTMLElement {
  constructor() {
    super();

    this.form = null;
    // Detect if we're on a search page or collection page
    this.sectionName = window.location.pathname.includes('/search') ? 'main-search' : 'main-collection';
    this.isLoading = false;
    this.filterContainer = document.querySelector('filters-drawer');
    // Support both collection and search grid containers
    this.gridContainer = document.querySelector('.main-collection-grid') || document.querySelector('.search-grid');
    this.headerContainer = document.querySelector('.filters-header');
    this.submitTimeout = null;
    this.userToggled = false;

    // Bind methods used as event handlers
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSortChange = this.handleSortChange.bind(this);
  }

  connectedCallback() {
    // Cache references
    this.form = this.querySelector('form');

    // If the drawer is marked active in markup but we're on mobile,
    // flip it to inactive as early as possible. CSS handles the initial hide
    // via opacity until we mark the drawer as initialized.
    const isMobile = isBelowBreakpoint('md');
    const isActive = this.getAttribute('data-active') === 'true';

    if (isMobile && isActive) {
      // Flip the active state for mobile
      this.setAttribute('data-active', 'false');
    }

    // Mark as initialized so CSS can remove the temporary mobile opacity hide
    this.setAttribute('data-initialized', 'true');

    // Attach form submit handler (override default behaviour)
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit);
      // Listen for change events on all form inputs (checkboxes, radios, number inputs)
      // Using event delegation so it works even after re-rendering
      this.form.addEventListener('change', this.handleInputChange);
    }

    // Listen for any toggle button clicks
    document.addEventListener('click', this.handleToggleClick);

    // Listen for sort_by select changes (may be in header, outside the form)
    document.addEventListener('change', this.handleSortChange);
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit);
      this.form.removeEventListener('change', this.handleInputChange);
    }

    document.removeEventListener('click', this.handleToggleClick);
    document.removeEventListener('change', this.handleSortChange);
    
    // Clear any pending submit timeout
    if (this.submitTimeout) {
      clearTimeout(this.submitTimeout);
      this.submitTimeout = null;
    }
  }

  /**
   * Handle clicks on any element with [data-filters-toggle]
   * This supports both the header "Filter" button and the close button inside the drawer.
   */
  handleToggleClick(event) {
    const toggle = event.target.closest('[data-filters-toggle]');
    if (!toggle) return;

    // If there are multiple drawers, prefer the one closest to the toggle
    const drawer = toggle.closest('filters-drawer') || document.querySelector('filters-drawer');
    if (!drawer || drawer !== this) return;

    event.preventDefault();

    const isActive = this.getAttribute('data-active') === 'true';
    this.setAttribute('data-active', isActive ? 'false' : 'true');
    this.userToggled = true;
  }

  /**
   * Handle sort_by select changes from the header (outside the form)
   */
  handleSortChange(event) {
    const select = event.target;
    if (select.name !== 'sort_by' || !this.form) return;

    // Update or create the sort_by input in the form
    let sortInput = this.form.querySelector('input[name="sort_by"]');
    if (!sortInput) {
      sortInput = document.createElement('input');
      sortInput.type = 'hidden';
      sortInput.name = 'sort_by';
      this.form.appendChild(sortInput);
    }
    sortInput.value = select.value;

    // Trigger form submission
    if (!this.isLoading) {
      this.handleSubmit(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  /**
   * Handle change events on form inputs (checkboxes, radios, number inputs)
   * Automatically submits the form after a brief delay to allow for rapid selections
   */
  handleInputChange(event) {
    const input = event.target;
    
    // Only handle filter inputs (ignore if clicking on a submit button or other non-filter elements)
    if (!input.matches('.filter-option, input[type="number"].price, input[name="sort_by"]')) {
      return;
    }

    // Clear any existing timeout
    if (this.submitTimeout) {
      clearTimeout(this.submitTimeout);
    }

    // Debounce the submission slightly to allow for rapid selections
    // This is especially useful for checkboxes where users might click multiple quickly
    this.submitTimeout = setTimeout(() => {
      if (this.form && !this.isLoading) {
        // Trigger form submission programmatically
        this.handleSubmit(new Event('submit', { bubbles: true, cancelable: true }));
      }
    }, 300);
  }

  /**
   * Intercept filter form submission and use section rendering API
   */
  async handleSubmit(event) {
    event.preventDefault();

    if (this.isLoading || !this.form) return;

    const formData = new FormData(this.form);
    const searchParams = new URLSearchParams(formData);

    // Reset pagination when filters change
    searchParams.delete('page');

    // For search pages, preserve the 'q' parameter from the current URL if it's not in the form
    if (this.sectionName === 'main-search') {
      const currentUrlParams = new URLSearchParams(window.location.search);
      const currentQuery = currentUrlParams.get('q');
      if (currentQuery && !searchParams.has('q')) {
        searchParams.set('q', currentQuery);
      }
    }

    const queryString = searchParams.toString();

    // URL used for section rendering (returns JSON with section HTML)
    const url = `${window.location.pathname}?${searchParams}`;
    const sectionUrl = `${url}${url.includes('?') ? '&' : '?'}sections=${this.sectionName}`;

    // URL shown in the address bar (without sections parameter)
    const browserUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}`;

    this.setLoading(true);

    try {
      const response = await fetch(sectionUrl, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`FiltersDrawer: Section request failed with status ${response.status}`);
      }

      const data = await response.json();
      
      if (!data) {
        throw new Error('FiltersDrawer: Section HTML not found in response');
      }

      this.renderSectionFromHTML(data);

      // Update URL to reflect active filters (without reloading)
      window.history.replaceState({}, '', browserUrl);
    } catch (error) {
      console.error('FiltersDrawer: Failed to update filters', error);
      // Fallback to full page submit if Ajax fails
      this.form.submit();
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Replace the current section's inner HTML with the freshly rendered section HTML
   */
  renderSectionFromHTML(data) {
    if (!data) return;

    const sectionHTML = data[this.sectionName];
    if (!sectionHTML) return;

    // Create a temporary container to parse the HTML fragment
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = sectionHTML;

    // Query from the temporary container
    const newFilters = tempContainer.querySelector('filters-drawer');
    // Support both collection and search grid containers
    const newGrid = tempContainer.querySelector('.main-collection-grid') || tempContainer.querySelector('.search-grid');
    const newHeader = tempContainer.querySelector('.filters-header');
    
    if (this.headerContainer && newHeader) {
      this.headerContainer.innerHTML = newHeader.innerHTML;
    }
      
    if (this.gridContainer && newGrid) {
      this.gridContainer.innerHTML = newGrid.innerHTML;
    }
    
    if (this.filterContainer && newFilters) {
      // Replace the drawer contents
      this.filterContainer.innerHTML = newFilters.innerHTML;
  
      // Re-bind form + submit handler to the new form element
      if (this.form) {
        this.form.removeEventListener('submit', this.handleSubmit);
        this.form.removeEventListener('change', this.handleInputChange);
      }
  
      this.form = this.filterContainer.querySelector('form');
      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit);
        this.form.addEventListener('change', this.handleInputChange);
      }
    }
  }

  setLoading(isLoading) {
    this.isLoading = isLoading;
    this.gridContainer.setAttribute('data-loading', isLoading ? 'true' : 'false');
  }

}

// Register the custom element
customElements.define('filters-drawer', FiltersDrawer);